import {
  Component,
  OnInit,
  inject,
  DestroyRef,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, ActivatedRoute, Router } from '@angular/router';
import { TabsModule } from 'primeng/tabs';
import { SkeletonModule } from 'primeng/skeleton';
import { TooltipModule } from 'primeng/tooltip';
import { DrawerModule } from 'primeng/drawer';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { DatePickerModule } from 'primeng/datepicker';
import { MenuModule } from 'primeng/menu';
import { finalize } from 'rxjs/operators';
import { CheckboxModule } from 'primeng/checkbox';
import { TagModule } from 'primeng/tag';
import { InputText } from 'primeng/inputtext';
import {
  ReactiveFormsModule,
  FormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MessageService, ConfirmationService, MenuItem } from 'primeng/api';
import {
  EfetivoService,
  EscalaService,
  PostoService,
  EfetivoEquipService,
  OcorrenciaService,
  AnexoEfetivoService,
} from '@core/services/domain-services';
import { forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { EmptyStateComponent } from '@shared/components/empty-state/empty-state.component';
import { BackButtonComponent } from '@shared/components/back-button/back-button.component';
import { BreadcrumbComponent } from '@shared/components/breadcrumb/breadcrumb.component';
import {
  Efetivo,
  Escala,
  Posto,
  EfetivoEquipamento,
  Ocorrencia,
  AnexoEfetivo,
  EnumGenero,
  EnumEstadoEquipamento,
  EnumOcorrenciaCriticidade,
  EnumTipoOcorrencia,
  ParentescoEnum,
} from '@core/models/models';
import { AnexoService } from '@shared/api/services/anexo.service';
import { compressImageIfNeeded } from '@shared/utils/compress-image.util';
import { TipoAnexoEfetivoService } from '@core/services/domain-services';
import { FileUpload } from 'primeng/fileupload';
import { AvatarModule } from 'primeng/avatar';
import { EditarEfetivoComponent } from '../editar-efetivo/editar-efetivo.component';
import {
  obterContactoEmergencia,
  telefoneParaLigacao,
} from './contacto-emergencia.util';

type state = 'Ativo' | 'Inativo' | 'Afastado' | 'Suspensos' | 'De_Folga';

export interface DocumentoEfetivo {
  id: number;
  idAnexo: number;
  nomeDocumento: string;
  urlPreview?: string;
}

type TipoEntradaTurnoDia = 'previsto' | 'continuacao';

interface EntradaTurnoDia {
  tipo: TipoEntradaTurnoDia;
  nomeTurno: string;
  horaInicio: string;
  horarioCompleto: string;
  termina: boolean;
}

interface DiaSemanaEscala {
  label: string;
  data: Date;
  numero: number;
  eHoje: boolean;
  entradas: EntradaTurnoDia[];
}

interface HistoricoPosto {
  id: number;
  descricao: string;
  codigo?: string;
  nomeCliente?: string;
  turno?: string;
  dataInicio?: string;
  dataTermino?: string;
  actual: boolean;
  turnosCumpridos?: number;
  faltas?: number;
  motivoSaida?: string;
}

type ApiRecord = Record<string, unknown>;

function asApiRecord(value: unknown): ApiRecord | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as ApiRecord)
    : null;
}

function readNumber(value: unknown): number | undefined {
  if (typeof value !== 'number' && typeof value !== 'string') return undefined;
  if (value === '') return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function readText(...values: unknown[]): string | undefined {
  const text = values.find(
    (value): value is string => typeof value === 'string' && value.trim().length > 0,
  );
  return text?.trim();
}

const DIAS_SEMANA_LABELS = [
  'Seg',
  'Ter',
  'Qua',
  'Qui',
  'Sex',
  'Sáb',
  'Dom',
] as const;
const TEXTO_SEMANA_PREVISTOS = 'turnos previstos';
const HORA_FIM_TURNO_24H = '08:30';

@Component({
  selector: 'app-detalhe-efetivo',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    TabsModule,
    SkeletonModule,
    TooltipModule,
    DrawerModule,
    ButtonModule,
    SelectModule,
    DatePickerModule,
    CheckboxModule,
    MenuModule,
    ReactiveFormsModule,
    FormsModule,
    TagModule,
    EmptyStateComponent,
    BackButtonComponent,
    EditarEfetivoComponent,
    InputText,
    AvatarModule,
  ],
  providers: [AnexoService],
  templateUrl: './detalhe-efetivo.component.html',
  styleUrl: './detalhe-efetivo.component.scss',
})
export class DetalheEfetivoComponent implements OnInit {
  private readonly routeAct = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly fb = inject(FormBuilder);
  private readonly efetivoSvc = inject(EfetivoService);
  private readonly tipoAnexoEfetivoSvc = inject(TipoAnexoEfetivoService);
  private readonly escalaService = inject(EscalaService);
  private readonly postoService = inject(PostoService);
  private readonly efetivoEquipSvc = inject(EfetivoEquipService);
  private readonly ocorrenciaSvc = inject(OcorrenciaService);
  private readonly anexoSvc = inject(AnexoEfetivoService);
  private readonly msgService = inject(MessageService);
  private readonly confirmSvc = inject(ConfirmationService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly anexoService = inject(AnexoService);
  private readonly cdr = inject(ChangeDetectorRef);

  protected readonly obterContactoEmergencia = obterContactoEmergencia;
  protected readonly telefoneParaLigacao = telefoneParaLigacao;

  efetivo: any | null = null;
  loadingEfetivo = false;
  currentEfetivoId = 0;
  private submitted = false;

  loadingHistorico = false;

  /**
   * Alguns backends devolvem sempre o mesmo URL para a fotografia (o ficheiro é
   * substituído no mesmo caminho), pelo que o browser serve a versão em cache
   * após reeditar. Adiciona-se um parâmetro derivado da data de actualização
   * para forçar o browser a ir buscar a imagem mais recente.
   */
  get fotoUrl(): string | null {
    const url = this.efetivo?.urlFotografia;
    if (!url) return null;
    const versao = this.efetivo?.dataAtualizacao ?? Date.now();
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}v=${encodeURIComponent(versao)}`;
  }

  // ── Turno checklist ──────────────────────────────────
  turnoChecklist: any[] = [];
  loadingTurnoChecklist = false;

  // ── Atribuir posto ──────────────────────────────────
  showAtribuirPosto = false;
  loadingAtribuir = false;
  atribuirForm!: FormGroup;
  postosList: any[] = [];
  loadingPostos = false;
  selectedPosto: any = null;
  filteredPostosList: any[] = [];
  searchPosto: string = '';

  coberturaOpts = [
    { label: '24 Horas', value: 1, icon: 'ri-time-line' },
    { label: '48 Horas', value: 2, icon: 'ri-time-line' },
  ];
  today = new Date();

  // ── Equipamentos ────────────────────────────────────
  efetivoEquipamentos: EfetivoEquipamento[] = [];
  loadingEquipamentos = false;

  // ── Ocorrências ─────────────────────────────────────
  ocorrencias: Ocorrencia[] = [];
  loadingOcorrencias = false;
  ocorrenciasDisponiveis = false;

  // ── Documentos ──────────────────────────────────────
  documentos: any[] = [];
  loadingDocumentos = false;
  showUploadDialog = false;
  uploadLoading = false;
  selectedDoc: File | null = null;
  tipoDocumento = '';
  designacao = '';
  tiposDocumento: any[] = [];

  getEquipamentoIcon(tipo: string): string {
    if (!tipo) return 'pi pi-box';
    const cleanTipo = tipo.toLowerCase();
    if (
      cleanTipo.includes('arma') ||
      cleanTipo.includes('pistola') ||
      cleanTipo.includes('revólver') ||
      cleanTipo.includes('revolver')
    ) {
      return 'pi pi-shield';
    }
    if (
      cleanTipo.includes('rádio') ||
      cleanTipo.includes('comunicação') ||
      cleanTipo.includes('comunicacao') ||
      cleanTipo.includes('ht') ||
      cleanTipo.includes('wifi')
    ) {
      return 'pi pi-wifi';
    }
    if (
      cleanTipo.includes('viatura') ||
      cleanTipo.includes('carro') ||
      cleanTipo.includes('mota') ||
      cleanTipo.includes('transporte')
    ) {
      return 'pi pi-car';
    }
    if (
      cleanTipo.includes('farda') ||
      cleanTipo.includes('colete') ||
      cleanTipo.includes('epi') ||
      cleanTipo.includes('uniforme')
    ) {
      return 'pi pi-user';
    }
    if (
      cleanTipo.includes('lanterna') ||
      cleanTipo.includes('iluminação') ||
      cleanTipo.includes('iluminacao') ||
      cleanTipo.includes('luz')
    ) {
      return 'pi pi-sun';
    }
    if (
      cleanTipo.includes('algema') ||
      cleanTipo.includes('contenção') ||
      cleanTipo.includes('contencao') ||
      cleanTipo.includes('bastão') ||
      cleanTipo.includes('bastao')
    ) {
      return 'pi pi-lock';
    }
    return 'pi pi-box';
  }

  formatTime(d: string | Date | undefined): string {
    if (!d) return '';
    if (typeof d === 'string') {
      if (/^\d{2}:\d{2}(:\d{2})?$/.test(d)) {
        return d.substring(0, 5);
      }
      if (d.includes('T') || d.includes('-') || d.includes('/')) {
        const date = new Date(d);
        if (!isNaN(date.getTime())) {
          const hrs = String(date.getHours()).padStart(2, '0');
          const mins = String(date.getMinutes()).padStart(2, '0');
          return `${hrs}:${mins}`;
        }
      }
      return d;
    }
    if (d instanceof Date && !isNaN(d.getTime())) {
      const hrs = String(d.getHours()).padStart(2, '0');
      const mins = String(d.getMinutes()).padStart(2, '0');
      return `${hrs}:${mins}`;
    }
    return '';
  }

  // ── Escala activa ──────────────────────────────────────────
  escalaActiva: any | null = null;

  // ── Postos passados (Tab 1) ────────────────────────────────
  postosPassados: HistoricoPosto[] = [];
  loadingPostosPassados = false;

  // ── Calendário semanal ─────────────────────────────────────
  semanaActual = new Date();
  diasSemana: DiaSemanaEscala[] = [];
  escalasSemana: Escala[] = [];
  loadingEscalasSemana = false;
  readonly skeletonsSemana = Array.from({ length: 7 }, (_, index) => index);
  private semanaRequestId = 0;

  // ── Tab navigation ─────────────────────────────────────────
  activeTabIndex = '0';
  mostrarDrawerEditar = false;

  ngOnInit(): void {
    this.initAtribuirForm();
    this.initCalendario();
    this.routeAct.paramMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        const id = Number(params.get('id'));
        if (id) {
          this.currentEfetivoId = id;
          this.loadEfetivo(id);
        }
      });
    this.loadTipoDocumentos();
    this.filteredPostosList = [...this.postosList];
  }

  filterPostos() {
    const term = this.searchPosto?.toLowerCase() || '';
    this.filteredPostosList = this.postosList.filter(
      (p) =>
        p?.nome.toLowerCase().includes(term) ||
        p?.codigo.toLowerCase().includes(term) ||
        p.endereco.toLowerCase().includes(term),
    );
  }

  selectPosto(posto: any) {
    this.selectedPosto = posto;
    this.atribuirForm.get('idPosto')?.setValue(posto.id);
  }

  initAtribuirForm(): void {
    this.atribuirForm = this.fb.group({
      idPosto: [null, Validators.required],
      dataInicio: [null, Validators.required],
      dataTermino: [null],
      turnoId: [1, Validators.required],
      observacoes: [null],
    });
  }

  loadEfetivo(id: number): void {
    this.loadingEfetivo = true;
    this.efetivoSvc
      .getById(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (ef: any) => {
          this.efetivo = ef.data;
          this.loadingEfetivo = false;
          this.cdr.detectChanges();
          this.loadEscalas(id);
          this.loadEscalasSemana();
          this.loadPostosPassados();
          this.loadTurnoChecklist(id);
          this.loadEfetivoEquipamentos(id);
          this.loadOcorrencias(id);
          this.loadDocumentos(id);
        },
        error: () => {
          this.loadingEfetivo = false;
          this.msgService.add({
            severity: 'error',
            summary: 'Erro',
            detail: 'Não foi possível carregar o efetivo.',
            life: 3000,
          });
        },
      });
  }

  loadTurnoChecklist(idEfetivo: number): void {
    this.loadingTurnoChecklist = true;
    this.efetivoEquipSvc
      .getTurnoChecklist({ EfetivoId: idEfetivo, pageNumber: 1, pageSize: 100 })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          this.turnoChecklist = res.data ?? [];
          this.loadingTurnoChecklist = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.turnoChecklist = [];
          this.loadingTurnoChecklist = false;
          this.cdr.detectChanges();
        },
      });
  }

  loadEfetivoEquipamentos(idEfetivo: number): void {
    this.loadingEquipamentos = true;
    this.efetivoEquipSvc
      .getAll({ idEfetivo, pageSize: 100 })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.efetivoEquipamentos = res.data ?? [];
          this.loadingEquipamentos = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.loadingEquipamentos = false;
        },
      });
  }

  loadOcorrencias(idEfetivo: number): void {
    this.loadingOcorrencias = true;
    this.ocorrenciasDisponiveis = false;
    this.ocorrenciaSvc
      .getAll({ idEfetivo, pageSize: 100 })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.ocorrencias = res.data ?? [];
          this.loadingOcorrencias = false;
          this.ocorrenciasDisponiveis = true;
          this.cdr.detectChanges();
        },
        error: () => {
          this.ocorrencias = [];
          this.loadingOcorrencias = false;
          this.msgService.add({
            severity: 'error',
            summary: 'Erro',
            detail: 'Não foi possível carregar as ocorrências do efectivo.',
            life: 3000,
          });
          this.cdr.detectChanges();
        },
      });
  }

  loadDocumentos(idEfetivo: number): void {
    this.loadingDocumentos = true;

    this.anexoSvc
      .getByEfetivo(idEfetivo)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          this.documentos = Array.isArray(res) ? res : (res?.data ?? []);

          if (!this.documentos.length) {
            this.loadingDocumentos = false;
            this.cdr.detectChanges();
            return;
          }

          const requests = this.documentos.map((doc: any) => {
            return this.anexoService.show(doc.idAnexo).pipe(
              map((anexo: any) => {
                const urlPreview = anexo?.file
                  ? this.createPreviewUrl(
                      anexo.file,
                      anexo.mimeType ?? 'application/octet-stream',
                    )
                  : null;

                return {
                  ...doc,
                  urlPreview,
                };
              }),

              catchError((err) => {
                console.error(`Erro ao carregar anexo ${doc.idAnexo}`, err);

                return of({
                  ...doc,
                  urlPreview: null,
                });
              }),
            );
          });

          forkJoin(requests).subscribe({
            next: (docsComPreview) => {
              this.documentos = docsComPreview;
              this.loadingDocumentos = false;
              this.cdr.detectChanges();
            },

            error: (err) => {
              console.error('Erro no forkJoin:', err);
              this.loadingDocumentos = false;
            },
          });

          this.cdr.detectChanges();
        },
        error: () => {
          this.loadingDocumentos = false;
        },
      });
  }

  getDocumentoMenuItems(doc: any): MenuItem[] {
    const items: MenuItem[] = [];

    if (doc?.urlPreview) {
      items.push({
        label: 'Visualizar',
        icon: 'pi pi-eye',
        command: () => {
          window.open(doc.urlPreview, '_blank', 'noopener,noreferrer');
        },
      });
    }

    items.push({
      label: 'Eliminar',
      icon: 'fas fa-trash-alt',
      styleClass: 'text-danger',
      command: () => {
        this.deleteDocumento(doc.id);
      },
    });

    return items;
  }

  onDocSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    this.selectedDoc = input.files[0];
  }

  onDocDrop(event: DragEvent): void {
    event.preventDefault();
    const file = event.dataTransfer?.files?.[0];
    if (file) this.selectedDoc = file;
  }

  uploadDocumento(): void {
    if (!this.selectedDoc || !this.tipoDocumento || this.uploadLoading) return;
    this.uploadLoading = true;
    const fd = new FormData();
    fd.append('idEfetivo', String(this.currentEfetivoId));
    fd.append('arquivo', this.selectedDoc, this.selectedDoc.name);
    fd.append('idTipoAnexo', this.tipoDocumento);
    fd.append('Designacao', this.designacao);

    this.anexoSvc
      .upload(fd)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.msgService.add({
            severity: 'success',
            summary: 'Sucesso',
            detail: 'Documento adicionado.',
            life: 3000,
          });
          this.showUploadDialog = false;
          this.selectedDoc = null;
          this.tipoDocumento = '';
          this.uploadLoading = false;
          this.loadDocumentos(this.currentEfetivoId);
        },
        error: () => {
          this.msgService.add({
            severity: 'error',
            summary: 'Erro',
            detail: 'Falha ao enviar documento.',
            life: 3000,
          });
          this.uploadLoading = false;
        },
      });
  }

  deleteDocumento(id: number): void {
    this.confirmSvc.confirm({
      header: 'Eliminar Documento',
      message: 'Este documento será eliminado. Deseja continuar?',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Eliminar',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.anexoSvc
          .delete(id)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.msgService.add({
                severity: 'success',
                summary: 'Eliminado',
                detail: 'Documento eliminado.',
                life: 3000,
              });
              // this.loadDocumentos(this.currentEfetivoId);
              this.loadEfetivo(this.efetivo.id);
            },
          });
      },
    });
  }

  // ── Atribuir posto ──────────────────────────────────
  openAtribuirPostoDialog(): void {
    this.showAtribuirPosto = true;
    this.submitted = false;
    this.atribuirForm.reset({ cobertura: 'DIURNO', escalaSubstituicao: false });
    if (!this.postosList.length) this.loadPostos();
  }

  loadPostos(): void {
    this.loadingPostos = true;
    this.postoService
      .getAll({ pageSize: 200 })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.postosList = res.data ?? [];
          this.loadingPostos = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.loadingPostos = false;
        },
      });
  }

  confirmarAtribuicao(): void {
    if (this.atribuirForm.invalid) {
      this.atribuirForm.markAllAsTouched();
      return;
    }
    if (this.submitted || this.loadingAtribuir) return;
    this.submitted = true;
    this.loadingAtribuir = true;

    const v = this.atribuirForm.value;

    const payload = {
      idEfetivo: this.currentEfetivoId,
      idPosto: v.idPosto,
      turnoId: v.turnoId,
      dataInicio: this.toISO(v.dataInicio),
      dataTermino: this.toISO(v.dataTermino),
      idsEfetivos: [this.currentEfetivoId],
    };

    this.escalaService
      .create(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.msgService.add({
            severity: 'success',
            summary: 'Sucesso',
            detail: 'Efetivo atribuído ao posto!',
            life: 3000,
          });
          this.showAtribuirPosto = false;
          this.loadingAtribuir = false;
          this.loadEfetivo(this.currentEfetivoId);
        },
        error: () => {
          this.msgService.add({
            severity: 'error',
            summary: 'Erro',
            detail: 'Falha ao atribuir posto.',
            life: 3000,
          });
          this.loadingAtribuir = false;
          this.submitted = false;
        },
      });
  }

  editarEfetivo(): void {
    this.mostrarDrawerEditar = true;
  }

  onEditarDrawerVisibleChange(visible: boolean): void {
    this.mostrarDrawerEditar = visible;
  }

  onEfetivoEditado(): void {
    this.loadEfetivo(this.currentEfetivoId);
  }

  // ── Helpers ──────────────────────────────────────────
  getEstadoSeverity(estado?: string): string {
    const map: Record<string, string> = {
      ACTIVO: 'success',
      SUSPENSO: 'warn',
      SANCIONADO: 'danger',
      INACTIVO: 'secondary',
    };
    return map[estado ?? 'ACTIVO'] ?? 'secondary';
  }

  formatDate(d: string | Date | undefined): string {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('pt-AO');
  }

  toISO(d: any): string {
    if (!d) return '';
    if (typeof d === 'string') {
      if (/^\d{4}-\d{2}-\d{2}/.test(d)) return d.slice(0, 10);
      d = new Date(d);
    }
    if (d instanceof Date && !isNaN(d.getTime())) {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    return '';
  }

  calcDuracao(ini: string | Date, fim: string | Date): string {
    const dias = Math.round(
      (new Date(fim).getTime() - new Date(ini).getTime()) / 86_400_000,
    );
    if (dias < 30) return `${dias} dia${dias !== 1 ? 's' : ''}`;
    const meses = Math.floor(dias / 30);
    return `${meses} mês${meses > 1 ? 'es' : ''}`;
  }

  getInitials(nome?: string): string {
    if (!nome) return '?';
    return nome
      .split(' ')
      .map((n) => n[0])
      .slice(0, 2)
      .join('')
      .toUpperCase();
  }

  getCriticidadeClass(c: any): string {
    const val = String(c ?? '').toUpperCase();
    if (val === 'ALTA' || val === '1') return 'badge-alta';
    if (val === 'MEDIA' || val === '2') return 'badge-media';
    if (val === 'URGENTE' || val === '3') return 'badge-urgente';
    return '';
  }

  getCriticidadeLabel(c: any): string {
    const val = String(c ?? '').toUpperCase();
    if (val === 'ALTA' || val === '1') return 'Alta';
    if (val === 'MEDIA' || val === '2') return 'Média';
    if (val === 'URGENTE' || val === '3') return 'Urgente';
    return c || '—';
  }

  getTipoOcorrenciaLabel(t: any): string {
    const val = String(t ?? '').toUpperCase();
    const map: Record<string, string> = {
      INTRUSAO: 'Intrusão',
      AUSENCIA: 'Ausência',
      AVARIA: 'Avaria',
      RONDA_INCOMPLETA: 'Ronda Incompleta',
      SUSPEITO: 'Suspeito',
      ACIDENTE: 'Acidente',
      OUTRO: 'Outro',
      '1': 'Incidente',
      '2': 'Falha de Equipamento',
      '3': 'Intrusão',
      '4': 'Emergência',
      '5': 'Outro',
    };
    return map[val] || t || '—';
  }

  getGeneroLabel(g: number | string | null | undefined): string {
    if (g === null || g === undefined || g === '') return '—';
    const valStr = String(g).toUpperCase();
    if (valStr === '1' || valStr === 'MASCULINO') return 'Masculino';
    if (valStr === '2' || valStr === 'FEMININO') return 'Feminino';
    return valStr || '—';
  }

  getParentescoLabel(val: number | string | null | undefined): string {
    if (val === null || val === undefined || val === '') return '—';
    const valStr = String(val).toUpperCase();
    if (valStr === '1' || valStr === 'CONJUGE') return 'Cônjuge';
    if (
      valStr === '2' ||
      valStr === 'PROGENITOR' ||
      valStr === 'PAI' ||
      valStr === 'MAE' ||
      valStr === 'MÃE'
    )
      return 'Progenitor';
    if (
      valStr === '3' ||
      valStr === 'IRMAO' ||
      valStr === 'IRMÃO' ||
      valStr === 'IRMÃ'
    )
      return 'Irmão/Irmã';
    if (valStr === '4' || valStr === 'FILHO' || valStr === 'FILHA')
      return 'Filho/Filha';
    if (valStr === '5' || valStr === 'OUTRO') return 'Outro';
    return valStr || '—';
  }

  getEstadoLabel(estado: number | string | null | undefined): string {
    let numericState: number | null = null;
    if (estado !== null && estado !== undefined && estado !== '') {
      numericState = Number(estado);
      if (isNaN(numericState)) {
        const stateMap: Record<string, number> = {
          Ativo: 0,
          ATIVO: 0,
          ACTIVO: 0,
          Inativo: 1,
          INATIVO: 1,
          INACTIVO: 1,
          Afastado: 2,
          AFASTADO: 2,
          Suspenso: 3,
          SUSPENSO: 3,
          Suspensos: 3,
          'De Folga': 4,
          'DE FOLGA': 4,
          De_Folga: 4,
          Férias: 5,
          FERIAS: 5,
        };
        numericState = stateMap[String(estado).trim()] ?? null;
      }
    }

    const map: Record<number, string> = {
      0: 'Ativo',
      1: 'Inativo',
      2: 'Afastado',
      3: 'Suspenso',
      4: 'De Folga',
      5: 'Férias',
    };
    return numericState !== null ? (map[numericState] ?? '—') : '—';
  }

  confirmarDelete(efetivo: any): void {
    const id = efetivo?.id;
    if (!id) return;
    this.confirmSvc.confirm({
      header: 'Eliminar Efetivo',
      message: `O efetivo "${efetivo?.nome}" será eliminado. Deseja continuar?`,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Eliminar',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.efetivoSvc
          .delete(id)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.msgService.add({
                severity: 'success',
                summary: 'Eliminado',
                detail: 'Efetivo eliminado.',
                life: 3000,
              });
              this.loadEfetivo(id);
            },
            error: () => {
              this.msgService.add({
                severity: 'error',
                summary: 'Erro',
                detail: 'Falha ao eliminar.',
                life: 3000,
              });
            },
          });
      },
    });
  }

  private createPreviewUrl(base64: string, mimeType: string): string {
    const binary = atob(base64);

    const bytes = new Uint8Array(binary.length);

    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }

    const blob = new Blob([bytes], {
      type: mimeType,
    });

    return URL.createObjectURL(blob);
  }

  loadTipoDocumentos(): void {
    this.tipoAnexoEfetivoSvc
      .getAll()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          this.tiposDocumento = res.data;
        },
        error: () => {
          this.msgService.add({
            severity: 'error',
            summary: 'Erro',
            detail: 'Não foi possível carregar os tipos de documentos.',
            life: 3000,
          });
        },
      });
  }

  private isValidFile(file: File): boolean {
    const allowedTypes = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
    const maxSize = 5 * 1024 * 1024; // 5 MB

    if (!allowedTypes.includes(file.type)) {
      this.msgService.add({
        severity: 'error',
        summary: 'Formato inválido',
        detail: 'Apenas PNG, JPEG, GIF e WEBP são permitidos.',
      });
      return false;
    }

    if (file.size > maxSize) {
      this.msgService.add({
        severity: 'error',
        summary: 'Arquivo muito grande',
        detail: 'O tamanho máximo é 5 MB.',
      });
      return false;
    }

    return true;
  }

  async onFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;

    if (!input.files?.length) {
      return;
    }

    const file = input.files[0];

    if (!this.isValidFile(file)) {
      input.value = '';
      return;
    }
    const designacao = 'Fotografia';
    const compressedFile = await compressImageIfNeeded(file);
    this.efetivoSvc.getTipoAnexo(designacao).subscribe({
      next: (res) => {
        const tipoAnexo = res;
        this.uploadPhoto(compressedFile, tipoAnexo);
      },
    });

    input.value = '';
  }

  private uploadPhoto(file: File, idTipoAnexo: number): void {
    const designacao = 'Fotografia';
    console.log(
      'Uploading photo for Efetivo ID:',
      this.efetivo.id,
      'File:',
      file.name,
      'TipoAnexo ID:',
      idTipoAnexo,
    );
    this.efetivoSvc
      .uploadFoto(this.efetivo.id, file, idTipoAnexo, designacao)
      .subscribe({
        next: (response) => {
          this.efetivo.urlFotografia =
            response.url || URL.createObjectURL(file);
          this.msgService.add({
            severity: 'success',
            summary: 'Sucesso',
            detail: 'Foto atualizada com sucesso!',
          });
          this.clearFileInput();
        },
        error: (err) => {
          console.error(err);
          this.msgService.add({
            severity: 'error',
            summary: 'Erro no upload',
            detail: 'Não foi possível carregar fotografia.',
          });
          this.clearFileInput();
        },
      });
  }

  private clearFileInput(): void {
    const fileUpload = document.querySelector('p-fileUpload') as any;
    if (fileUpload && fileUpload.clear) {
      fileUpload.clear();
    }
  }

  // ── Estado badge helpers ─────────────────────────────
  getEstadoBadgeClass(estado: number | string | null | undefined): string {
    let numericState: number | null = null;
    if (estado !== null && estado !== undefined && estado !== '') {
      numericState = Number(estado);
      if (isNaN(numericState)) {
        const stateMap: Record<string, number> = {
          Ativo: 0,
          ATIVO: 0,
          ACTIVO: 0,
          Inativo: 1,
          INATIVO: 1,
          INACTIVO: 1,
          Afastado: 2,
          AFASTADO: 2,
          Suspenso: 3,
          SUSPENSO: 3,
          Suspensos: 3,
          'De Folga': 4,
          'DE FOLGA': 4,
          De_Folga: 4,
          Férias: 5,
          FERIAS: 5,
        };
        numericState = stateMap[String(estado).trim()] ?? null;
      }
    }

    const map: Record<number, string> = {
      0: 'estado-ativo',
      1: 'estado-inativo',
      2: 'estado-afastado',
      3: 'estado-suspenso',
      4: 'estado-folga',
      5: 'estado-ferias',
    };
    return numericState !== null
      ? (map[numericState] ?? 'estado-inativo')
      : 'estado-inativo';
  }

  getEstadoIcon(estado: number | string | null | undefined): string {
    let numericState: number | null = null;
    if (estado !== null && estado !== undefined && estado !== '') {
      numericState = Number(estado);
      if (isNaN(numericState)) {
        const stateMap: Record<string, number> = {
          Ativo: 0,
          ATIVO: 0,
          ACTIVO: 0,
          Inativo: 1,
          INATIVO: 1,
          INACTIVO: 1,
          Afastado: 2,
          AFASTADO: 2,
          Suspenso: 3,
          SUSPENSO: 3,
          Suspensos: 3,
          'De Folga': 4,
          'DE FOLGA': 4,
          De_Folga: 4,
          Férias: 5,
          FERIAS: 5,
        };
        numericState = stateMap[String(estado).trim()] ?? null;
      }
    }

    const map: Record<number, string> = {
      0: 'pi pi-check-circle',
      1: 'pi pi-ban',
      2: 'pi pi-minus-circle',
      3: 'pi pi-exclamation-circle',
      4: 'pi pi-clock',
      5: 'pi pi-calendar',
    };
    return numericState !== null
      ? (map[numericState] ?? 'pi pi-minus')
      : 'pi pi-minus';
  }

  // ── Posto activo ─────────────────────────────────────
  temPostoActivo(): boolean {
    return this.escalaActiva !== null;
  }

  get isTurnoIniciado(): boolean {
    if (!this.temPostoActivo()) return false;

    if (
      this.escalaActiva?.isCheckIn === true ||
      this.escalaActiva?.estaEmServico === true ||
      this.efetivo?.isCheckIn === true ||
      this.efetivo?.emServico === true ||
      this.efetivo?.estaEmServico === true
    ) {
      return true;
    }

    const escalas =
      this.escalaActiva?.escalas ?? this.efetivo?.escalasPostos?.escalas ?? [];
    if (Array.isArray(escalas) && escalas.length > 0) {
      return escalas.some(
        (esc: any) =>
          esc.isCheckIn === true ||
          esc.estaEmServico === true ||
          !!esc.dataCheckIn ||
          !!esc.horaInicioReal ||
          !!esc.horaCheckIn ||
          esc.estadoPresenca === 'Presente' ||
          esc.estadoOperacional === 'presente' ||
          esc.estadoOperacional === 'atraso',
      );
    }
    return false;
  }

  loadEscalas(idEfetivo: number): void {
    if (
      this.efetivo &&
      this.efetivo.escalasPostos &&
      this.efetivo.escalasPostos.posto
    ) {
      this.escalaActiva = this.efetivo.escalasPostos;
    } else {
      this.escalaActiva = null;
    }
    this.cdr.detectChanges();
  }

  loadPostosPassados(): void {
    this.loadingPostosPassados = true;
    const escalasPostos =
      asApiRecord(this.efetivo?.escalasPostos) ?? {};
    const fontes: unknown[] = [
      this.efetivo?.postosPassados,
      this.efetivo?.historicoPostos,
      this.efetivo?.postos,
      [escalasPostos],
      escalasPostos['postosPassados'],
      escalasPostos['historicoPostos'],
      escalasPostos['postos'],
      escalasPostos['escalas'],
    ].flatMap((fonte: unknown): unknown[] =>
      Array.isArray(fonte) ? fonte : [],
    );

    const postoActual =
      asApiRecord(escalasPostos['posto']) ??
      asApiRecord(this.efetivo?.postoAtual) ??
      asApiRecord(this.efetivo?.postoActual) ??
      asApiRecord(this.efetivo?.posto);
    const idPostoActual = readNumber(postoActual?.['id']);
    const postos = new Map<number, HistoricoPosto>();

    for (const fonte of fontes) {
      const item = asApiRecord(fonte);
      if (!item) continue;

      const posto = asApiRecord(item['posto']) ?? item;
      const id =
        readNumber(posto['id']) ??
        readNumber(item['idPosto']) ??
        readNumber(item['postoId']);
      if (id === undefined) continue;

      const actual =
        posto === postoActual ||
        item['actual'] === true ||
        ['ativo', 'activo', 'em curso'].includes(
          readText(item['estado'], item['status'])?.toLowerCase() ?? '',
        );
      const faltas = item['faltas'];
      const historicoPosto: HistoricoPosto = {
        id,
        descricao:
          readText(
            posto['descricao'],
            posto['nome'],
            item['nomePosto'],
            item['nome'],
          ) ?? `Posto #${id}`,
        codigo: readText(posto['codigo'], item['codigoPosto']),
        nomeCliente: readText(
          posto['nomeCliente'],
          posto['clienteNome'],
        ),
        turno: readText(
          item['regime'],
          item['turno'],
          item['nomeTurno'],
          item['turnoContrato'],
          posto['turno'],
        ),
        dataInicio: readText(
          item['dataInicio'],
          item['dataInicioContrato'],
        ),
        dataTermino: readText(item['dataTermino'], item['dataFim']),
        actual: actual || id === idPostoActual,
        turnosCumpridos: readNumber(item['turnosCumpridos']),
        faltas: Array.isArray(faltas)
          ? faltas.length
          : readNumber(faltas),
        motivoSaida: readText(item['motivoSaida']),
      };

      const existente = postos.get(id);
      postos.set(
        id,
        existente
          ? {
              ...historicoPosto,
              ...existente,
              actual: actual || existente.actual,
              codigo: existente.codigo ?? historicoPosto.codigo,
              nomeCliente:
                existente.nomeCliente ?? historicoPosto.nomeCliente,
              turno: existente.turno ?? historicoPosto.turno,
              dataInicio:
                existente.dataInicio ?? historicoPosto.dataInicio,
              dataTermino:
                existente.dataTermino ?? historicoPosto.dataTermino,
              turnosCumpridos:
                existente.turnosCumpridos ??
                historicoPosto.turnosCumpridos,
              faltas: existente.faltas ?? historicoPosto.faltas,
              motivoSaida:
                existente.motivoSaida ?? historicoPosto.motivoSaida,
            }
          : historicoPosto,
      );
    }

    if (postoActual && idPostoActual !== undefined) {
      const actualExistente = postos.get(idPostoActual);
      const postoActualHistorico: HistoricoPosto = {
        id: idPostoActual,
        descricao:
          readText(postoActual['descricao'], postoActual['nome']) ??
          actualExistente?.descricao ??
          `Posto #${idPostoActual}`,
        codigo:
          readText(postoActual['codigo']) ?? actualExistente?.codigo,
        nomeCliente:
          readText(
            postoActual['nomeCliente'],
            postoActual['clienteNome'],
          ) ?? actualExistente?.nomeCliente,
        turno:
          readText(postoActual['turno']) ?? actualExistente?.turno,
        dataInicio: actualExistente?.dataInicio,
        dataTermino: actualExistente?.dataTermino,
        actual: true,
        turnosCumpridos: actualExistente?.turnosCumpridos,
        faltas: actualExistente?.faltas,
        motivoSaida: actualExistente?.motivoSaida,
      };
      postos.set(idPostoActual, postoActualHistorico);
    }

    this.postosPassados = Array.from(postos.values()).sort((a, b) => {
      if (a.actual !== b.actual) return a.actual ? -1 : 1;
      return this.getTimestamp(b.dataInicio) - this.getTimestamp(a.dataInicio);
    });
    this.loadingPostosPassados = false;
    this.cdr.detectChanges();
  }

  getTotalTurnosCumpridos(): number | null {
    if (this.postosPassados.length === 0) return null;
    if (
      this.postosPassados.some(
        (posto) => posto.turnosCumpridos === undefined,
      )
    ) {
      return null;
    }
    return this.postosPassados.reduce(
      (total, posto) => total + (posto.turnosCumpridos ?? 0),
      0,
    );
  }

  getOcorrenciasPosto(posto: HistoricoPosto): number {
    return this.ocorrencias.filter(
      (ocorrencia) =>
        ocorrencia.idPosto === posto.id ||
        ocorrencia.posto?.id === posto.id,
    ).length;
  }

  getDuracaoPostoActual(): string {
    const postoActual = this.postosPassados.find((posto) => posto.actual);
    return postoActual ? this.getDuracaoPosto(postoActual) : '—';
  }

  getDuracaoPosto(posto: HistoricoPosto): string {
    const inicio = this.getDate(posto.dataInicio);
    const fim = posto.actual
      ? this.startOfDay(new Date())
      : this.getDate(posto.dataTermino);
    if (!inicio || !fim) return '—';

    const diferenca = fim.getTime() - inicio.getTime();
    if (diferenca < 0) return '—';
    const dias = Math.floor(diferenca / 86400000) + 1;
    return `${dias} ${dias === 1 ? 'dia' : 'dias'}`;
  }

  private getTimestamp(value?: string): number {
    return this.getDate(value)?.getTime() ?? 0;
  }

  private getDate(value?: string): Date | null {
    if (!value) return null;
    const datePart = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    const date = datePart
      ? new Date(
          Number(datePart[1]),
          Number(datePart[2]) - 1,
          Number(datePart[3]),
        )
      : new Date(value);
    if (
      Number.isNaN(date.getTime()) ||
      (datePart &&
        (date.getFullYear() !== Number(datePart[1]) ||
          date.getMonth() !== Number(datePart[2]) - 1 ||
          date.getDate() !== Number(datePart[3])))
    ) {
      return null;
    }
    return this.startOfDay(date);
  }

  initCalendario(): void {
    const hoje = this.startOfDay(new Date());
    const dia = hoje.getDay();
    this.semanaActual = this.addDays(hoje, dia === 0 ? -6 : 1 - dia);
    this.diasSemana = [];
  }

  loadEscalasSemana(): void {
    if (!this.currentEfetivoId) {
      this.escalasSemana = [];
      this.buildDiasSemana();
      return;
    }

    const fimSemana = this.addDays(this.semanaActual, 6);
    fimSemana.setHours(23, 59, 59, 999);
    const requestId = ++this.semanaRequestId;
    this.loadingEscalasSemana = true;

    this.escalaService
      .getAll({
        EfetivoId: this.currentEfetivoId,
        DataInicioAte: this.toApiDateTime(fimSemana),
        DataTerminoDe: this.toApiDateTime(this.semanaActual),
        pageNumber: 1,
        pageSize: 100,
      })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          if (requestId !== this.semanaRequestId) return;
          this.loadingEscalasSemana = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (response) => {
          if (requestId !== this.semanaRequestId) return;
          this.escalasSemana = response.data ?? [];
          this.buildDiasSemana();
        },
        error: () => {
          if (requestId !== this.semanaRequestId) return;
          this.escalasSemana = [];
          this.buildDiasSemana();
        },
      });
  }

  buildDiasSemana(): void {
    const hoje = this.startOfDay(new Date());
    this.diasSemana = DIAS_SEMANA_LABELS.map((label, index) => {
      const data = this.addDays(this.semanaActual, index);
      return {
        label,
        data,
        numero: data.getDate(),
        eHoje: data.getTime() === hoje.getTime(),
        entradas: this.obterEntradasDia(this.escalasSemana, data),
      };
    });
    this.cdr.detectChanges();
  }

  private obterEntradasDia(
    escalas: Escala[],
    dia: Date,
  ): EntradaTurnoDia[] {
    return escalas
      .map((escala) => this.criarEntradaDia(escala, dia))
      .filter((entrada): entrada is EntradaTurnoDia => entrada !== null)
      .sort((a, b) => {
        if (a.tipo !== b.tipo) {
          return a.tipo === 'previsto' ? -1 : 1;
        }
        return a.horaInicio.localeCompare(b.horaInicio);
      });
  }

  private criarEntradaDia(
    escala: Escala,
    dia: Date,
  ): EntradaTurnoDia | null {
    const inicio = this.parseEscalaDate(
      escala.dataInicioPrevista ?? escala.dataInicio,
    );
    if (!inicio) return null;

    const inicioDia = this.startOfDay(inicio);
    const diaNormalizado = this.startOfDay(dia);
    const fim = this.obterFimEscala(escala, inicio);
    const fimDia = fim ? this.startOfDay(fim) : null;
    const horaInicio = this.formatarHora(escala.horaInicio ?? inicio);
    const horaFim = this.formatarHora(
      escala.horaFim ?? escala.horaTermino ?? escala.turno?.horaFim ?? fim,
    );
    const atravessaMeiaNoite =
      !!fimDia && fimDia.getTime() > inicioDia.getTime();
    const nomeTurno = this.getNomeTurno(escala);

    if (diaNormalizado.getTime() === inicioDia.getTime()) {
      return {
        tipo: 'previsto',
        nomeTurno,
        horaInicio,
        horarioCompleto:
          atravessaMeiaNoite && horaFim
            ? `${horaInicio} → ${horaFim}`
            : horaInicio,
        termina: false,
      };
    }

    if (
      !fimDia ||
      diaNormalizado.getTime() <= inicioDia.getTime() ||
      diaNormalizado.getTime() > fimDia.getTime()
    ) {
      return null;
    }

    const termina = diaNormalizado.getTime() === fimDia.getTime();
    return {
      tipo: 'continuacao',
      nomeTurno,
      horaInicio,
      horarioCompleto: termina && horaFim ? `até ${horaFim}` : 'em curso',
      termina,
    };
  }

  private obterFimEscala(escala: Escala, inicio: Date): Date | null {
    const fimExplicito = this.parseEscalaDate(
      escala.dataTerminoPrevista ?? escala.dataTermino ?? escala.dataFim,
    );
    if (fimExplicito) return fimExplicito;

    const horaFim = this.obterHoraFim(escala);
    if (!horaFim) return null;

    const [horas, minutos] = horaFim.split(':').map(Number);
    if (!Number.isFinite(horas) || !Number.isFinite(minutos)) return null;

    const fim = new Date(inicio);
    fim.setHours(horas, minutos, 0, 0);
    if (fim.getTime() <= inicio.getTime()) {
      fim.setDate(fim.getDate() + 1);
    }
    return fim;
  }

  private obterHoraFim(escala: Escala): string {
    const horaExplicita = this.formatarHora(
      escala.horaFim ?? escala.horaTermino ?? escala.turno?.horaFim,
    );
    if (horaExplicita) return horaExplicita;

    const nomeTurno = this.getNomeTurno(escala).toUpperCase();
    return nomeTurno.includes('24') ? HORA_FIM_TURNO_24H : '';
  }

  private getNomeTurno(escala: Escala): string {
    return (
      escala.nomeTurno?.trim() ||
      escala.turno?.nome?.trim() ||
      escala.turno?.designacao?.trim() ||
      'Turno'
    );
  }

  private parseEscalaDate(value: string | Date | null | undefined): Date | null {
    if (!value) return null;
    const data = value instanceof Date ? new Date(value) : new Date(value);
    return Number.isNaN(data.getTime()) ? null : data;
  }

  private formatarHora(value: string | Date | null | undefined): string {
    if (!value) return '';
    if (typeof value === 'string') {
      const match = value.match(/(\d{2}):(\d{2})/);
      if (match) return `${match[1]}:${match[2]}`;
    }
    const data = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(data.getTime())) return '';
    return `${String(data.getHours()).padStart(2, '0')}:${String(
      data.getMinutes(),
    ).padStart(2, '0')}`;
  }

  semanaAnterior(): void {
    this.semanaActual = this.addDays(this.semanaActual, -7);
    this.loadEscalasSemana();
  }

  semanaSeguinte(): void {
    this.semanaActual = this.addDays(this.semanaActual, 7);
    this.loadEscalasSemana();
  }

  getLabelSemana(): string {
    const fim = this.addDays(this.semanaActual, 6);
    const inicioTexto = `${this.pad2(this.semanaActual.getDate())}/${this.pad2(
      this.semanaActual.getMonth() + 1,
    )}`;
    const fimTexto = `${this.pad2(fim.getDate())}/${this.pad2(
      fim.getMonth() + 1,
    )}`;
    const anos =
      this.semanaActual.getFullYear() === fim.getFullYear()
        ? String(fim.getFullYear())
        : `${this.semanaActual.getFullYear()}/${fim.getFullYear()}`;
    return `${inicioTexto} a ${fimTexto}/${anos}`;
  }

  getResumoSemana(): string {
    return `${this.getLabelSemana()} · ${TEXTO_SEMANA_PREVISTOS}`;
  }

  private startOfDay(date: Date): Date {
    const result = new Date(date);
    result.setHours(0, 0, 0, 0);
    return result;
  }

  private addDays(date: Date, amount: number): Date {
    const result = this.startOfDay(date);
    result.setDate(result.getDate() + amount);
    return result;
  }

  private pad2(value: number): string {
    return String(value).padStart(2, '0');
  }

  private toApiDateTime(date: Date): string {
    return `${date.getFullYear()}-${this.pad2(date.getMonth() + 1)}-${this.pad2(
      date.getDate(),
    )}T${this.pad2(date.getHours())}:${this.pad2(
      date.getMinutes(),
    )}:${this.pad2(date.getSeconds())}`;
  }

  // ── Turno checklist helpers ──────────────────────────
  getTurnoChecklistItem(equipamentoId: number): any | null {
    return (
      this.turnoChecklist.find((c) => c.equipamentoId === equipamentoId) ?? null
    );
  }

  verDetalhePosto(idPosto: number): void {
    this.router.navigate(['/admin/postos', idPosto]);
  }

  getSeverityFunction(
    estado: state,
  ):
    | 'success'
    | 'info'
    | 'warn'
    | 'danger'
    | 'secondary'
    | 'contrast'
    | null
    | undefined {
    switch (estado) {
      case 'Ativo':
        return 'success';
      case 'Inativo':
        return 'warn';
      case 'Afastado':
        return 'secondary';
      case 'Suspensos':
        return 'danger';
      case 'De_Folga':
        return 'contrast';
      default:
        return 'info';
    }
  }
}
